import { useCallback, useMemo } from 'react';

import { useMachine } from '@xstate/react';

import { useTelemetry } from '@order/Telemetry';

import { type useOrderingMachine } from '../../../../../../machine';
import { bagFeaturedUpsellMachine, type BagFeaturedUpsellContext } from '../../machine';
import { useFetchBagFeaturedUpsellProduct } from '../useFetchBagFeaturedUpsellProduct';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitBagFeaturedUpsellMachine = (params: Params) => {
  const { addUpsell } = params;

  const { track } = useTelemetry();
  const { fetchProduct } = useFetchBagFeaturedUpsellProduct();

  // ─── Actions ───────────────────────────────────────────────────────────────

  const trackView = useCallback(
    (actionParams: MachineActionParams) => {
      track('bag.featured_upsell.view', getTelemetryPayload(actionParams.context));
    },
    [track],
  );

  const trackAdd = useCallback(
    (actionParams: MachineActionParams) => {
      track('bag.featured_upsell.add.pressed', getTelemetryPayload(actionParams.context));
    },
    [track],
  );

  const trackDismiss = useCallback(
    (actionParams: MachineActionParams) => {
      track('bag.featured_upsell.dismiss.pressed', getTelemetryPayload(actionParams.context));
    },
    [track],
  );

  const addProductToBag = useCallback(
    (actionParams: MachineActionParams) => {
      const featuredProduct = actionParams.context.product;

      if (!featuredProduct) {
        return;
      }

      addUpsell({
        productId: featuredProduct.id,
        productName: featuredProduct.name,
        quantity: 1,
        isPersonalized: false,
      });
    },
    [addUpsell],
  );

  // ─── Machine ───────────────────────────────────────────────────────────────

  const machine = useMemo(
    () =>
      bagFeaturedUpsellMachine.provide({
        actors: { fetchProduct },
        actions: { trackView, trackAdd, trackDismiss, addProductToBag },
      }),
    [fetchProduct, trackView, trackAdd, trackDismiss, addProductToBag],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return useMachine(machine);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTelemetryPayload(context: BagFeaturedUpsellContext) {
  return { featuredUpsell: context.product?.name ?? '' };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = Pick<ReturnType<typeof useOrderingMachine>, 'addUpsell'>;

type MachineActionParams = {
  context: BagFeaturedUpsellContext;
};
